<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">أقسام المدونة</h1>
          <p class="ds-page-header__subtitle">إدارة أقسام تصنيف مقالات المدونة</p>
        </div>
      </div>

      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__header-icon"><i :class="`pi ${editingId ? 'pi-pencil' : 'pi-plus'}`"></i></div>
          <span class="form-card__title">{{ editingId ? 'تعديل القسم' : 'إضافة قسم جديد' }}</span>
        </div>
        <div class="form-card__body">
          <form @submit.prevent="save" class="row g-4 align-items-end">
            <div class="col-lg-4 col-12">
              <div class="form-field">
                <label class="field-label">اسم القسم <span class="required">*</span></label>
                <InputText v-model="form.title" class="w-100" placeholder="مثال: نصائح دراسية" />
              </div>
            </div>
            <div class="col-lg-3 col-12">
              <div class="form-field">
                <label class="field-label">الرابط المختصر <span class="optional">اختياري</span></label>
                <InputText v-model="form.slug" class="w-100" dir="ltr" />
              </div>
            </div>
            <div class="col-lg-3 col-12">
              <div class="form-field">
                <label class="field-label">الوصف <span class="optional">اختياري</span></label>
                <InputText v-model="form.description" class="w-100" />
              </div>
            </div>
            <div class="col-lg-2 col-12 d-flex gap-2">
              <button type="submit" class="btn-submit" :disabled="saving || !form.title.trim()" style="flex:1">
                <i class="pi pi-check"></i> {{ editingId ? 'حفظ' : 'إضافة' }}
              </button>
              <button v-if="editingId" type="button" class="btn-cancel" @click="resetForm">إلغاء</button>
            </div>
          </form>
        </div>
      </div>

      <Loading v-if="loading" />
      <template v-else>
        <div v-if="!categories.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-tags"></i></div>
          <h3 class="empty-state__title">لا توجد أقسام</h3>
          <p class="empty-state__desc">ابدأ بإضافة أول قسم لتصنيف مقالات المدونة.</p>
        </div>

        <div v-else class="ds-table-wrap table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">القسم</th>
                <th>الرابط المختصر</th>
                <th class="text-center">عدد المقالات</th>
                <th class="text-center pe-3">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in categories" :key="c.id">
                <td class="ps-3">
                  <div class="fw-bold">{{ c.title }}</div>
                  <small class="text-muted" v-if="c.description">{{ c.description }}</small>
                </td>
                <td dir="ltr" class="text-muted">{{ c.slug }}</td>
                <td class="text-center">{{ c.posts_count ?? 0 }}</td>
                <td class="text-center pe-3">
                  <div class="icon-action-group justify-content-center">
                    <button class="icon-action-btn icon-action-btn--edit" v-tooltip.top="'تعديل'" @click="edit(c)">
                      <i class="pi pi-pencil"></i>
                    </button>
                    <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف'" @click="remove(c)">
                      <i class="pi pi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import InputText from 'primevue/inputtext';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';

function emptyForm() {
  return { title: '', slug: '', description: '' };
}

export default {
  name: 'BlogCategories',
  components: { Breadcrumb, Loading, InputText },
  data() {
    return {
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'المدونة', path: '/blog' },
        { name: 'الأقسام', path: '/blog/categories' },
      ],
      categories: [],
      loading: true,
      saving: false,
      editingId: null,
      form: emptyForm(),
    };
  },
  methods: {
    async fetchCategories() {
      this.loading = true;
      try {
        const { data } = await axios.get('/admin/blog-categories');
        this.categories = data.data ?? [];
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'تعذر تحميل الأقسام', timer: 2000, showConfirmButton: false });
      } finally {
        this.loading = false;
      }
    },
    edit(c) {
      this.editingId = c.id;
      this.form = { title: c.title, slug: c.slug, description: c.description ?? '' };
    },
    resetForm() {
      this.editingId = null;
      this.form = emptyForm();
    },
    async save() {
      this.saving = true;
      try {
        if (this.editingId) {
          await axios.put(`/admin/blog-categories/${this.editingId}`, this.form);
        } else {
          await axios.post('/admin/blog-categories', this.form);
        }
        await Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
        this.resetForm();
        this.fetchCategories();
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      } finally {
        this.saving = false;
      }
    },
    async remove(c) {
      const res = await Swal.fire({
        title: 'حذف القسم', html: `سيتم نقل <strong>${c.title}</strong> إلى سلة المحذوفات ويمكن استرجاعه لاحقاً.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await axios.delete(`/admin/blog-categories/${c.id}`);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
        this.fetchCategories();
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.fetchCategories();
  },
};
</script>
